import { useCallback, useEffect, useState } from 'react';

/**
 * The one way a screen loads a resource.
 *
 * WHY A SHARED HOOK
 * ------------------
 * Every screen in this console reads something from the API and then renders one
 * of four states — loading, error, empty, ready. Written inline four times over
 * it produced four slightly different loaders, and the difference showed up as
 * a screen that swallowed a failure and rendered "nothing here" instead of the
 * error. One loader means one behaviour.
 *
 * WHY THE STATE IS NOT SET SYNCHRONOUSLY
 * --------------------------------------
 * `setState` called directly in an effect body cascades renders: the effect
 * runs, sets state, the component re-renders, and the guard that decided to
 * fetch has not changed. The loader below awaits FIRST and only then touches
 * state, so a fetch that is already in flight never causes a second render
 * pass before it has anything to show.
 *
 * THE LOADER MUST BE STABLE
 * --------------------------
 * `load` is expected to be a `useCallback`. The effect depends on it, so an
 * unstable loader would refetch on every render. The dependency array names it
 * explicitly rather than taking a spread, so the lint rule can verify it.
 */

export type AsyncResource<T> = {
  readonly data: T | null;
  readonly error: Error | null;
  readonly isLoading: boolean;
  /** Re-runs the loader. Use it for a retry after a failure. */
  readonly reload: () => void;
};

export function useAsyncResource<T>(load: () => Promise<T>): AsyncResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const result = await load();
        if (cancelled) return;
        setData(result);
        setError(null);
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [load, attempt]);

  const reload = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  return { data, error, isLoading, reload };
}
